import { Tariff } from './types.tariff'

const { BASIC, STANDARD, PREMIUM } = Tariff

export enum FeatureAvailability {
  DEVELOPMENT = 'development',
  PRIVATE_BETA = 'private-beta',
  PUBLIC_BETA = 'public-beta',
  HIDDEN = 'hidden'
}

const { DEVELOPMENT, PRIVATE_BETA, PUBLIC_BETA, HIDDEN } = FeatureAvailability

export const featuresSchema = {
  TARIFF: {
    type: 'string',
    title: 'Tariff',
    default: STANDARD,
    enum: [BASIC, STANDARD, PREMIUM],
    defaultAvailability: PRIVATE_BETA
  },
  API_URL: {
    type: 'string',
    title: 'API URL',
    defaultAvailability: HIDDEN
  },
  VERSIONS: {
    type: 'boolean',
    title: 'Versions',
    defaultsPerTariff: { [BASIC]: false, [STANDARD]: true, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  NLP_EDITOR: {
    type: 'boolean',
    title: 'NLP editor',
    defaultsPerTariff: { [BASIC]: true, [STANDARD]: true, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  INTERCOM_ID: {
    type: 'string',
    title: 'Intercom ID',
    defaultAvailability: HIDDEN
  },
  ENVIRONMENTS: {
    type: 'boolean',
    title: 'Environments',
    defaultsPerTariff: { [BASIC]: false, [STANDARD]: false, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  NLP_PROVIDERS: {
    type: 'boolean',
    title: 'NLP provider',
    defaultsPerTariff: { [BASIC]: false, [STANDARD]: false, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  SHARE_MAX_ROLE: {
    type: 'string',
    title: 'Max share role',
    default: 'comment',
    enum: ['view', 'comment', 'copywrite', 'edit', 'admin'],
    defaultsPerTariff: { [STANDARD]: 'copywrite', [PREMIUM]: 'copywrite' },
    defaultAvailability: HIDDEN
  },
  SHARE_TOKEN_MAX_ROLE: {
    type: 'string',
    title: 'Max share token role',
    default: 'comment',
    enum: ['view', 'comment', 'copywrite', 'edit'],
    defaultsPerTariff: { [BASIC]: 'comment', [STANDARD]: 'copywrite', [PREMIUM]: 'copywrite' },
    defaultAvailability: HIDDEN
  },
  CHANNELS_EDITOR: {
    type: 'boolean',
    title: 'Channels editor',
    defaultsPerTariff: { [BASIC]: true, [STANDARD]: true, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  CUSTOM_INTEGRATION_SERVICES: {
    type: 'string',
    title: 'Custom integration services',
    defaultAvailability: HIDDEN
  },
  NEW_VALIDATOR_STORAGE_MODE: {
    type: 'boolean',
    title: 'New validator storage mode',
    defaultAvailability: HIDDEN
  },
  VOICE_BUILDER: {
    type: 'boolean',
    title: 'Voice builder menu item',
    description: 'Adds voice builder item to the menu next to the logo',
    defaultAvailability: HIDDEN
  },
  NEW_STEP_NAMES: {
    type: 'boolean',
    title: 'New step names',
    description: 'Enable new step names',
    defaultAvailability: HIDDEN
  },
  FILE_DOWNLOAD: {
    title: 'Download files',
    type: 'boolean',
    description: 'File download from media gallery context menu',
    defaultsPerTariff: { [BASIC]: true, [STANDARD]: true, [PREMIUM]: true },
    defaultAvailability: HIDDEN
  },
  SSML_VALIDATION: {
    title: 'SSML text validation',
    type: 'boolean',
    description: 'Evaluation whether the text is SSML valid',
    defaultAvailability: PRIVATE_BETA
  },
  NEW_INLINE_EDITABLE: {
    type: 'boolean',
    title: 'Storage autocomplete',
    description: 'Enable list menu for selecting storages inside step text',
    defaultAvailability: PUBLIC_BETA
  },
  STEP_VISIT_STATISTICS: {
    type: 'boolean',
    title: 'Step visit statistics',
    description: 'Displays statistics how many times was step node visited',
    defaultAvailability: DEVELOPMENT
  },
  NLP_INTENTS_WITH_DESCRIPTION: {
    type: 'boolean',
    title: 'Intents with description',
    description: 'Input classification based on intent description.',
    defaultAvailability: HIDDEN
  },
  NLP_KB_CHAT_PROVIDER: {
    type: 'boolean',
    title: 'KB Chat NLP provider',
    defaultAvailability: HIDDEN
  },
  UNIQUE_BOT_ID_VALIDATION: {
    type: 'boolean',
    title: 'Enforce unique IDs validation',
    description: 'Disallow saving json editor/bot when two entities have the same id',
    defaultAvailability: PRIVATE_BETA
  },
  CROSS_USER_UI_BLOCKING: {
    type: 'boolean',
    title: 'Lock bot when settings opened',
    description:
      "Lock Designer when other users are using JSON editor. This guarantees users do not overwrite each other's changes.",
    defaultAvailability: PUBLIC_BETA,
    defaultsPerTariff: { [BASIC]: true, [STANDARD]: true, [PREMIUM]: true }
  },
  GENERATE_BOT_NAME: {
    type: 'boolean',
    title: 'Generate bot name',
    description: 'Generate bot from additional bot properties',
    defaultAvailability: HIDDEN
  },
  ALLOW_SETTING_ANY_HOSTING_VERSION: {
    type: 'boolean',
    title: 'Allow setting any hosting version',
    description: 'In bot settings form, allow updating/downgrading hosting to any existing version',
    defaultAvailability: DEVELOPMENT
  },
  NLP_TESTER: {
    type: 'boolean',
    title: 'NLP Tester',
    description: 'Enables test option for NLP models and fuzzy question steps',
    defaultAvailability: DEVELOPMENT
  },
  STEP_NLP_DESIGNER: {
    type: 'boolean',
    title: 'Step NLP Designer',
    description: 'Enables NLP designer for closed question steps inside voice bots',
    defaultAvailability: PRIVATE_BETA
  },
  GDPR_SETTINGS: {
    type: 'boolean',
    title: 'GDPR settings',
    description: 'Enables GDPR settings in bot settings',
    defaultAvailability: PRIVATE_BETA
  },
  NLP_CUSTOM_ENTITY_OPTION_REPOSITORY: {
    type: 'boolean',
    title: 'NLP entity option repository',
    description: 'Allow NLP custom entity options from repository',
    defaultAvailability: HIDDEN
  },
  CONVERSATIONS_LIST: {
    type: 'boolean',
    title: 'Conversations list',
    description: 'List of all conversations with bot',
    defaultAvailability: DEVELOPMENT
  },
  TRY_PANEL_LOGGER: {
    type: 'boolean',
    title: 'Try panel logger',
    description: 'Show webchat logs of various types results from hosting',
    defaultAvailability: PRIVATE_BETA
  },
  LAUNCH_APPLICATION_TYPE_SETTINGS: {
    type: 'boolean',
    title: 'Launch application type settings',
    description: 'Allow user to choose application type to Function App or Web App during launch',
    defaultAvailability: PRIVATE_BETA
  },
  PROMPT_WITH_ANSWERS: {
    type: 'boolean',
    title: 'Prompt with answers',
    description: 'Enable adding answers to prompt steps',
    defaultAvailability: DEVELOPMENT
  },
  MEDIA_GALLERY_MIGRATION: {
    type: 'boolean',
    title: 'Media gallery migration',
    description: 'Enable migrate media gallery files from hosting to server',
    defaultAvailability: PRIVATE_BETA
  },
  DISPLAY_DISABLED_VERSIONS: {
    type: 'boolean',
    title: 'Display disabled versions',
    description:
      'Some versions were historically created with faulty state/logic. They have since been disabled, and hidden by default. This option displays those versions.',
    defaultAvailability: DEVELOPMENT
  },
  BOT_TESTER: {
    type: 'boolean',
    title: 'Bot tester',
    description:
      'Enable bot tester product. This product allows user to test, if production bot is behaving as expected.',
    defaultAvailability: DEVELOPMENT
  },
  HIDE_DASHBOARD_TUTORIALS: {
    type: 'boolean',
    title: 'Hide dashboard tutorials',
    defaultAvailability: HIDDEN
  },
  LIST_TEST_BOTS: {
    type: 'boolean',
    title: 'List test bots',
    description: 'Include test bots in Bot select + Dashboard',
    defaultAvailability: DEVELOPMENT
  },
  NLP_CHAT_QUERIES: {
    type: 'boolean',
    title: 'NLP chat queries',
    description: 'Enables NLP global chat queries list.',
    defaultAvailability: PRIVATE_BETA
  },
  BOT_TESTER_SCENARIOS: {
    type: 'boolean',
    title: 'Bot tester scenarios',
    description:
      'Save existing conversations as bot tester scenarios. Replay those scenarios in tester at any time',
    defaultAvailability: DEVELOPMENT
  },
  ADVANCED_FILE_PREVIEW: {
    type: 'boolean',
    title: 'Advanced file preview',
    description: 'Display indicators showing if non-image files exist',
    defaultAvailability: DEVELOPMENT
  },
  STANDALONE_KNOWLEDGE_BASE: {
    type: 'boolean',
    title: 'Standalone Knowledge Base',
    description:
      'Enable option to define standalone knowledge bases and use them also in Chat model or AI steps',
    defaultAvailability: DEVELOPMENT
  },
  LLM_MODEL_SETTINGS_DOCS_LINK: {
    type: 'boolean',
    title: 'LLM model settings docs link',
    defaultAvailability: DEVELOPMENT
  }
} as const
